import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { User } from "@supabase/supabase-js";
import type { Expense, UserSettings } from "../domains/types";
import { recordAmountUsage, updateCategoryPattern } from "../utils/usage-tracker";

// Database types matching our Supabase schema
interface DatabaseExpense {
    id: string;
    user_id: string;
    amount: number;
    currency: string;
    date: string;
    category: string;
    category_id: string | null;
    description: string | null;
    created_at: string;
    updated_at: string | null;
}

interface DatabaseUserSettings {
    user_id: string;
    default_currency: string;
    created_at: string;
    updated_at: string;
}

// Create Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

function createSupabaseClient(): SupabaseClient {
    if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error(
            "Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY"
        );
    }
    return createClient(supabaseUrl, supabaseAnonKey);
}

// Lazy-initialize client to allow graceful handling when env vars are missing
let supabaseInstance: SupabaseClient | null = null;

/**
 * Get the Supabase client instance (singleton pattern).
 * Lazily initializes the client on first access.
 * 
 * @returns {SupabaseClient} The Supabase client instance
 * @throws {Error} If environment variables are not configured
 * 
 * @example
 * ```typescript
 * const supabase = getSupabase();
 * const { data } = await supabase.from('expenses').select('*');
 * ```
 */
export function getSupabase(): SupabaseClient {
    if (!supabaseInstance) {
        supabaseInstance = createSupabaseClient();
    }
    return supabaseInstance;
}

/**
 * Check if Supabase environment variables are configured.
 * Useful for graceful degradation or showing setup instructions.
 * 
 * @returns {boolean} True if VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set
 * 
 * @example
 * ```typescript
 * if (!isSupabaseConfigured()) {
 *   console.error('Please configure Supabase credentials');
 * }
 * ```
 */
export function isSupabaseConfigured(): boolean {
    return Boolean(supabaseUrl && supabaseAnonKey);
}

// Export singleton instance
export const supabase = getSupabase();

/**
 * Transform database row to application Expense type.
 * Converts snake_case database columns to camelCase TypeScript properties.
 * 
 * @private
 * @param {DatabaseExpense} row - Raw database row from Supabase
 * @returns {Expense} Transformed expense object
 */
function toExpense(row: DatabaseExpense): Expense {
    return {
        id: row.id,
        userId: row.user_id,
        amount: row.amount,
        currency: row.currency,
        date: row.date,
        category: row.category,
        categoryId: row.category_id ?? undefined,
        description: row.description ?? undefined,
        createdAt: row.created_at,
        updatedAt: row.updated_at ?? undefined,
    };
}

/**
 * Fetch all expenses for the current authenticated user.
 * Results are ordered by date (most recent first).
 * 
 * @returns {Promise<Expense[]>} Array of user's expenses
 * @throws {Error} If database query fails or user is not authenticated
 * 
 * @example
 * ```typescript
 * const expenses = await getExpenses();
 * console.log(`Found ${expenses.length} expenses`);
 * ```
 */
export async function getExpenses(): Promise<Expense[]> {
    const supabase = getSupabase();
    const { data, error } = await supabase
        .from("expenses")
        .select("*")
        .order("date", { ascending: false });

    if (error) {
        throw new Error(`Failed to fetch expenses: ${error.message}`);
    }

    return (data as DatabaseExpense[]).map(toExpense);
}

/**
 * Create a new expense for the current authenticated user.
 * 
 * Automatically records usage patterns (amount and category) for smart defaults.
 * Usage tracking failures are logged but don't affect expense creation.
 * 
 * @param {Omit<Expense, "id" | "createdAt" | "userId">} expense - Expense data (id, createdAt, userId auto-generated)
 * @returns {Promise<Expense>} The created expense with generated id and timestamps
 * @throws {Error} If user is not authenticated or database operation fails
 * 
 * @example
 * ```typescript
 * const newExpense = await addExpense({
 *   amount: 49.99,
 *   currency: 'USD',
 *   date: '2026-01-06',
 *   category: 'Food & Dining',
 *   categoryId: 'cat-food-123',
 *   description: 'Lunch at cafe'
 * });
 * ```
 */
export async function addExpense(
    expense: Omit<Expense, "id" | "createdAt" | "userId">
): Promise<Expense> {
    const supabase = getSupabase();
    const user = await getCurrentUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
        .from("expenses")
        .insert({
            user_id: user.id,
            amount: expense.amount,
            currency: expense.currency,
            date: expense.date,
            category: expense.category,
            category_id: expense.categoryId || null,
            description: expense.description || null,
        })
        .select()
        .single();

    if (error) {
        throw new Error(`Failed to add expense: ${error.message}`);
    }

    // Record usage patterns for smart defaults (fire and forget)
    if (expense.categoryId) {
        Promise.all([
            recordAmountUsage(supabase, user.id, expense.amount, expense.currency),
            updateCategoryPattern(supabase, user.id, expense.categoryId),
        ]).catch(err => {
            // Log but don't fail the expense creation
            console.warn('Failed to record usage patterns:', err);
        });
    }

    return toExpense(data as DatabaseExpense);
}

/**
 * Update an existing expense by ID.
 * 
 * Only updates provided fields (partial update). Verifies the expense belongs
 * to the current user for security.
 * 
 * @param {string} id - The expense ID to update
 * @param {Partial<Omit<Expense, "id" | "userId" | "createdAt">>} updates - Fields to update (partial)
 * @returns {Promise<Expense>} The updated expense
 * @throws {Error} If expense not found, user doesn't own it, or database operation fails
 * 
 * @example
 * ```typescript
 * const updated = await updateExpense('exp-123', {
 *   amount: 59.99,
 *   description: 'Updated description'
 * });
 * ```
 */
export async function updateExpense(
    id: string,
    updates: Partial<Omit<Expense, "id" | "userId" | "createdAt">>
): Promise<Expense> {
    const supabase = getSupabase();
    const user = await getCurrentUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    // Build update object with only provided fields
    const updateData: Partial<{
        amount: number;
        currency: string;
        date: string;
        category: string;
        category_id: string | null;
        description: string | null;
    }> = {};

    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.currency !== undefined) updateData.currency = updates.currency;
    if (updates.date !== undefined) updateData.date = updates.date;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId || null;
    if (updates.description !== undefined) updateData.description = updates.description || null;

    const { data, error } = await supabase
        .from("expenses")
        .update(updateData)
        .eq("id", id)
        .eq("user_id", user.id) // Security: ensure user owns this expense
        .select()
        .single();

    if (error) {
        throw new Error(`Failed to update expense: ${error.message}`);
    }

    if (!data) {
        throw new Error("Expense not found or you don't have permission to update it");
    }

    return toExpense(data as DatabaseExpense);
}

/**
 * Delete an expense by ID.
 * 
 * Permanently removes the expense from the database. This operation cannot be undone.
 * 
 * @param {string} id - The expense ID to delete
 * @returns {Promise<void>}
 * @throws {Error} If database operation fails
 * 
 * @example
 * ```typescript
 * await deleteExpense('exp-123');
 * console.log('Expense deleted successfully');
 * ```
 */
export async function deleteExpense(id: string): Promise<void> {
    const supabase = getSupabase();

    const { error } = await supabase.from("expenses").delete().eq("id", id);

    if (error) {
        throw new Error(`Failed to delete expense: ${error.message}`);
    }
}

/**
 * Get the currently authenticated user from Supabase Auth.
 * 
 * Returns null if no user is signed in. Does not throw on unauthenticated state.
 * 
 * @returns {Promise<User | null>} The authenticated user or null if not signed in
 * 
 * @example
 * ```typescript
 * const user = await getCurrentUser();
 * if (user) {
 *   console.log('Logged in as:', user.email);
 * } else {
 *   console.log('Not authenticated');
 * }
 * ```
 */
export async function getCurrentUser(): Promise<User | null> {
    const supabase = getSupabase();
    const {
        data: { user },
    } = await supabase.auth.getUser();
    return user;
}

/**
 * Sign out the current user.
 * 
 * Clears the authentication session and redirects user to login.
 * 
 * @returns {Promise<void>}
 * @throws {Error} If sign out operation fails
 * 
 * @example
 * ```typescript
 * await signOut();
 * console.log('User signed out successfully');
 * ```
 */
export async function signOut(): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase.auth.signOut();
    if (error) {
        throw new Error(`Failed to sign out: ${error.message}`);
    }
}

/**
 * Transform database row to application UserSettings type.
 * Converts snake_case database columns to camelCase TypeScript properties.
 * 
 * @private
 * @param {DatabaseUserSettings} row - Raw database row from Supabase
 * @returns {UserSettings} Transformed user settings object
 */
function toUserSettings(row: DatabaseUserSettings): UserSettings {
    return {
        userId: row.user_id,
        defaultCurrency: row.default_currency,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
}

/**
 * Fetch user settings (preferences) for the current authenticated user.
 * 
 * Returns null if no settings exist yet (first-time user). This is expected
 * and should be handled by creating default settings.
 * 
 * @returns {Promise<UserSettings | null>} User settings or null if not yet created
 * @throws {Error} If user is not authenticated or database query fails (other than not found)
 * 
 * @example
 * ```typescript
 * const settings = await getUserSettings();
 * const currency = settings?.defaultCurrency || 'USD';
 * ```
 */
export async function getUserSettings(): Promise<UserSettings | null> {
    const supabase = getSupabase();
    const user = await getCurrentUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", user.id)
        .single();

    if (error) {
        // If no settings exist yet, return null (not an error)
        if (error.code === "PGRST116") {
            return null;
        }
        throw new Error(`Failed to fetch user settings: ${error.message}`);
    }

    return toUserSettings(data as DatabaseUserSettings);
}

/**
 * Update user settings (creates if doesn't exist - upsert pattern).
 * 
 * Automatically updates the `updated_at` timestamp. Uses upsert to handle
 * both first-time creation and subsequent updates.
 * 
 * @param {Partial<Omit<UserSettings, "userId" | "createdAt" | "updatedAt">>} settings - Settings to update
 * @returns {Promise<UserSettings>} The updated user settings
 * @throws {Error} If user is not authenticated or database operation fails
 * 
 * @example
 * ```typescript
 * const updated = await updateUserSettings({
 *   defaultCurrency: 'EUR'
 * });
 * console.log('Currency preference updated to:', updated.defaultCurrency);
 * ```
 */
export async function updateUserSettings(
    settings: Partial<Omit<UserSettings, "userId" | "createdAt" | "updatedAt">>
): Promise<UserSettings> {
    const supabase = getSupabase();
    const user = await getCurrentUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
        .from("user_settings")
        .upsert(
            {
                user_id: user.id,
                default_currency: settings.defaultCurrency,
                updated_at: new Date().toISOString(),
            },
            {
                onConflict: "user_id",
            }
        )
        .select()
        .single();

    if (error) {
        throw new Error(`Failed to update user settings: ${error.message}`);
    }

    return toUserSettings(data as DatabaseUserSettings);
}
